import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import { motion } from "framer-motion";

import Logo3D from "./Logo3D";
import logoUrl from "../../images/Recurso 7.svg?url";

const MotionBox = motion(Box);

const onboardingSteps = [
  { icon: EmailOutlinedIcon, label: "Cuenta creada", done: true },
  { icon: LanguageRoundedIcon, label: "Página en preparación", active: true },
  { icon: Inventory2OutlinedIcon, label: "Cargar contenido" },
  { icon: RocketLaunchRoundedIcon, label: "Publicar" },
];

export default function HeroSection({
  onStartClick,
  onExploreClick,
  primaryLabel = "Crear mi página gratis",
  isAuthenticated = false,
  userName = "",
}) {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: 14, md: 15 },
        pb: { xs: 8, md: 11 },
        background:
          "radial-gradient(circle at 82% 18%, rgba(243,90,58,.15), transparent 32%), radial-gradient(circle at 8% 76%, rgba(39,84,105,.12), transparent 30%), linear-gradient(180deg, #FFF9F6 0%, #FFFFFF 70%)",
        "&::after": {
          content: '\"\"',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.35,
          backgroundImage:
            "linear-gradient(rgba(25,36,47,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(25,36,47,.04) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
          maskImage: "linear-gradient(to bottom, black, transparent 78%)",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.02fr) minmax(420px, .98fr)" },
            alignItems: "center",
            gap: { xs: 7, md: 7, lg: 10 },
          }}
        >
          <Box>
            <Chip
              icon={<CheckCircleRoundedIcon />}
              label={
                isAuthenticated
                  ? `Sesión activa${userName ? ` · ${userName}` : ""}`
                  : "Activá tu proyecto web gratis"
              }
              sx={{
                mb: 2.5,
                height: 36,
                borderRadius: 999,
                bgcolor: "rgba(243,90,58,.1)",
                color: "#C83E21",
                fontWeight: 900,
                "& .MuiChip-icon": { color: "#F35A3A" },
              }}
            />

            <Typography
              component="h1"
              sx={{
                maxWidth: 730,
                color: "#19242F",
                fontWeight: 950,
                letterSpacing: "-.055em",
                lineHeight: 0.98,
                fontSize: { xs: "2.75rem", sm: "3.7rem", md: "4.55rem", lg: "5.15rem" },
              }}
            >
              Creá tu página.
              <Box component="span" sx={{ display: "block", color: "#F35A3A" }}>
                Hacé crecer tu negocio.
              </Box>
            </Typography>

            <Typography
              sx={{
                mt: 3,
                maxWidth: 650,
                color: "rgba(25,36,47,.7)",
                fontSize: { xs: "1.05rem", md: "1.18rem" },
                lineHeight: 1.7,
                fontWeight: 550,
              }}
            >
              WebProfit te guía desde el registro con tu email hasta la publicación. Configurá tu marca, cargá productos o servicios y avanzá siempre con un próximo paso visible.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
              <Button
                onClick={onStartClick}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: 56,
                  px: 3.2,
                  borderRadius: 999,
                  bgcolor: "#F35A3A",
                  textTransform: "none",
                  fontWeight: 900,
                  fontSize: "1rem",
                  boxShadow: "0 16px 34px rgba(243,90,58,.3)",
                  "&:hover": {
                    bgcolor: "#DF4728",
                    transform: "translateY(-2px)",
                    boxShadow: "0 20px 40px rgba(243,90,58,.34)",
                  },
                }}
              >
                {primaryLabel}
              </Button>
              <Button
                onClick={onExploreClick}
                variant="outlined"
                size="large"
                sx={{
                  minHeight: 56,
                  px: 3,
                  borderRadius: 999,
                  color: "#19242F",
                  borderColor: "rgba(25,36,47,.22)",
                  textTransform: "none",
                  fontWeight: 850,
                  "&:hover": {
                    borderColor: "#19242F",
                    bgcolor: "rgba(25,36,47,.035)",
                  },
                }}
              >
                Ver cómo funciona
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={1.2} sx={{ mt: 3 }}>
              {["Sin tarjeta", "Configuración guiada", "Podés continuar después"].map((item) => (
                <Stack key={item} direction="row" alignItems="center" spacing={0.7}>
                  <CheckCircleRoundedIcon sx={{ color: "#2A9D67", fontSize: 18 }} />
                  <Typography sx={{ color: "rgba(25,36,47,.68)", fontWeight: 750, fontSize: ".9rem" }}>
                    {item}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <MotionBox
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            sx={{ position: "relative", minHeight: { xs: 500, sm: 560, md: 610 } }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: { xs: "4% 2% 16%", md: "2% 0 14%" },
                borderRadius: { xs: 8, md: 10 },
                background: "linear-gradient(145deg, #19242F 0%, #263C4B 100%)",
                boxShadow: "0 36px 90px rgba(25,36,47,.26)",
                overflow: "hidden",
                "&::before": {
                  content: '\"\"',
                  position: "absolute",
                  width: 330,
                  height: 330,
                  borderRadius: "50%",
                  top: -120,
                  right: -90,
                  background: "rgba(243,90,58,.28)",
                  filter: "blur(1px)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: { xs: 250, sm: 330, md: 350 },
                  height: { xs: 250, sm: 330, md: 350 },
                  top: { xs: 38, sm: 30, md: 36 },
                  left: "50%",
                  transform: "translateX(-50%)",
                  filter: "drop-shadow(0 28px 35px rgba(0,0,0,.28))",
                }}
              >
                <Logo3D svgUrl={logoUrl} depth={12} />
              </Box>
            </Box>

            <Paper
              elevation={0}
              sx={{
                position: "absolute",
                left: { xs: 0, sm: 18, md: -22 },
                right: { xs: 12, sm: "auto" },
                bottom: { xs: 10, md: 18 },
                width: { sm: 360, md: 390 },
                borderRadius: 4,
                p: 2.2,
                border: "1px solid rgba(25,36,47,.09)",
                boxShadow: "0 22px 55px rgba(25,36,47,.18)",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.3 }}>
                <Box>
                  <Typography sx={{ color: "#F35A3A", fontWeight: 900, fontSize: ".76rem", letterSpacing: ".08em", textTransform: "uppercase" }}>
                    Tu recorrido
                  </Typography>
                  <Typography sx={{ color: "#19242F", fontWeight: 900 }}>Paso 2 de 4</Typography>
                </Box>
                <Chip label="En progreso" size="small" sx={{ bgcolor: "rgba(243,90,58,.1)", color: "#C83E21", fontWeight: 850 }} />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={50}
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor: "rgba(25,36,47,.08)",
                  "& .MuiLinearProgress-bar": { borderRadius: 99, bgcolor: "#F35A3A" },
                }}
              />
              <Stack spacing={1.05} sx={{ mt: 2 }}>
                {onboardingSteps.map(({ icon: Icon, label, done, active }) => (
                  <Stack key={label} direction="row" alignItems="center" spacing={1.1}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        color: done || active ? "#fff" : "rgba(25,36,47,.42)",
                        bgcolor: done ? "#2A9D67" : active ? "#F35A3A" : "rgba(25,36,47,.07)",
                      }}
                    >
                      <Icon sx={{ fontSize: 17 }} />
                    </Box>
                    <Typography sx={{ flexGrow: 1, color: "#19242F", fontWeight: done || active ? 850 : 700, fontSize: ".9rem" }}>
                      {label}
                    </Typography>
                    {done && <CheckCircleRoundedIcon sx={{ color: "#2A9D67", fontSize: 19 }} />}
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                position: "absolute",
                top: { xs: 2, md: 12 },
                right: { xs: 0, md: -12 },
                p: 1.5,
                pr: 2,
                borderRadius: 3,
                border: "1px solid rgba(25,36,47,.08)",
                boxShadow: "0 18px 42px rgba(25,36,47,.16)",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.2}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2.3, bgcolor: "rgba(42,157,103,.12)", color: "#238558", display: "grid", placeItems: "center" }}>
                  <CheckCircleRoundedIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "#19242F", fontSize: ".88rem" }}>Siempre sabés qué sigue</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,.6)", fontSize: ".76rem" }}>Sin perderte en el panel</Typography>
                </Box>
              </Stack>
            </Paper>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
}
